import { Button } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import { isSafeInternalPath } from "../utils/navigation";

const LOCATION_HISTORY_KEY = "app:locationHistory";

const toPathString = (loc) => {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  return `${loc.pathname || ""}${loc.search || ""}${loc.hash || ""}`;
};

const isSamePath = (a, b) => toPathString(a) === toPathString(b);

/**
 * Uniform back button used across the app.
 * - If `to` or `location.state.from` is provided, it navigates there.
 * - Otherwise, it goes back in browser history.
 * - If browser history cannot go back, it falls back to a stored short history stack.
 */
export default function BackButton({
  to,
  children = "Go Back",
  variant = "light",
  className = "btn btn-light my-3",
  fallback = "/",
  useStoredHistory = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const readStoredHistory = () => {
    try {
      const raw = sessionStorage.getItem(LOCATION_HISTORY_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const popStoredPrevious = () => {
    const currentPath = toPathString(location);
    const history = readStoredHistory();

    // Remove current page entries from the top.
    while (history.length && isSamePath(history[history.length - 1], currentPath)) {
      history.pop();
    }

    const target = history.length ? history.pop() : null;

    try {
      sessionStorage.setItem(LOCATION_HISTORY_KEY, JSON.stringify(history));
      if (history.length) {
        sessionStorage.setItem("app:lastLocation", JSON.stringify(history[history.length - 1]));
      }
    } catch {}

    return target;
  };

  const stateFrom = location.state?.from;
  const finalTo = to || (isSafeInternalPath(stateFrom) ? stateFrom : null);

  const handleClick = () => {
    if (finalTo) {
      navigate(finalTo);
      return;
    }

    if (document.referrer?.startsWith(window.location.origin)) {
      try {
        const referrerUrl = new URL(document.referrer);
        const referrerPath = `${referrerUrl.pathname}${referrerUrl.search}`;

        if (isSafeInternalPath(referrerPath)) {
          navigate(referrerPath);
          return;
        }
      } catch {
        // Ignore malformed referrers and continue to stored history/fallback.
      }
    }

    const storedPrevious = useStoredHistory ? popStoredPrevious() : null;
    if (storedPrevious) {
      navigate(storedPrevious, { replace: true, state: storedPrevious.state });
      return;
    }

    navigate(fallback);
  };

  return (
    <Button onClick={handleClick} variant={variant} className={className}>
      {children}
    </Button>
  );
}
